import React, { useState, useEffect, useContext } from "react";
import { Card, Table, Button, Form, OverlayTrigger, Tooltip} from "react-bootstrap";
import NotyfContext from "../contexts/NotyfContext";
import { X, Check, Edit2, Trash2, Watch, Archive, MessageSquare, File} from "react-feather";
import { UserSettingsContext } from "../contexts/UserSettingsContext";
import { useTable, useSortBy, useFilters} from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";

import authenticatedFetch from "../utils/fetch"
import EditableExpires from "./EditableExpires";
import LinkedDocumentsDisplay from "./LinkedDocumentsDisplay";
import LinkedDocumentSelect from "./LinkedDocumentSelect";
import { getDocumentDisplayLabel } from "./linkedDocumentUtils";
import { customExpiresFilter, ExpiresColumnCheckboxFilter, caseInsensitiveSort, getReminderSentDisplay } from "./reactTableUtils";

const formatCurrency = (val) => {
  if (val === null || val === undefined) return "";

  // Convert val to string (comes from server as number)
  val = val.toString();

  const cleaned = val.replace(/[^\d.]/g, "");
  if (!cleaned) return "";

  const [int, dec] = cleaned.split(".");

  const formattedInt = int
    ? Number(int).toLocaleString()
    : "";

  if (dec !== undefined) {
    return `${formattedInt}.${dec.slice(0, 2)}`;
  }

  return formattedInt;
};

const MalpracticeInsurance = ({ id, providerId, carrierName, policy, carrierAddress, perClaimLimit, aggregateLimit, startDate, endDate, notes, archived, reminderSent, documents, refreshList, canEdit, onCancelEdit, startInEditMode=false }) => {
  const [editing, setEditing] = useState(startInEditMode);
  const [localCarrierName, setLocalCarrierName] = useState(carrierName);
  const [localPolicy, setLocalPolicy] = useState(policy);
  const [localCarrierAddress, setLocalCarrierAddress] = useState(carrierAddress);
  const [localPerClaimLimit, setLocalPerClaimLimit] = useState(formatCurrency(perClaimLimit));
  const [localAggregateLimit, setLocalAggregateLimit] = useState(formatCurrency(aggregateLimit));
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [localNotes, setLocalNotes] = useState(notes);
  const [localArchived, setLocalArchived] = useState(archived);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documentFilenames, setDocumentFilenames] = useState([]);
  
  const { isH3Admin, authorities, documentsRefresh, setDocumentsRefresh } = useContext(UserSettingsContext);

  const notyf = useContext(NotyfContext);

  const setInitialDocumentFilenames = () => {
    let initialDocumentFilenames = []
    let initialSelectedDocuments = []
    if (documents) // undefined if we're excluding the column due to permissions
    {
      for (var i=0; i<documents.length; i++)
      {
        initialDocumentFilenames.push(documents[i].filename);
        initialSelectedDocuments.push({value: documents[i].filename, label: getDocumentDisplayLabel(documents[i])});
      }
    }

    setDocumentFilenames(initialDocumentFilenames);
    setSelectedDocuments(initialSelectedDocuments);
  }

  useEffect(() => {
      setInitialDocumentFilenames();
  }, [id, providerId, documents]);

  useEffect(() => {
    if (!editing) {
      setLocalPolicy(policy);
      setLocalStartDate(startDate);
      setLocalEndDate(endDate);
      setLocalArchived(archived);
      setInitialDocumentFilenames();
    }
  }, [policy, startDate, endDate, archived, documents]);

  const handleDocumentChanged = (documents, e) => {
    setSelectedDocuments(documents);

    let newDocumentFilenames = []
    for (var i=0; i<documents.length; i++)
    {
      newDocumentFilenames.push(documents[i].value);
    }

    setDocumentFilenames(newDocumentFilenames);
  }

  function handleDeleteClicked(e) {
    e.preventDefault();
    if (window.confirm("Are you sure you want to delete this item?"))
    {
      authenticatedFetch({
        method: "DELETE",
        path: "malpracticeinsurance/" + id,
        successHandler: function(result) {
          refreshList();
          setDocumentsRefresh(documentsRefresh + 1);
          notyf.open({type: "success", message: "Deleted!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
        },
        errorHandler: function(error) {
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
    }
  }

  function handleEditClicked(e) {
    e.preventDefault();
    setEditing(true);
  }

  function cancelEdit(e) {
    e.preventDefault();
    setEditing(false);
    setLocalCarrierName(carrierName);
    setLocalPolicy(policy);
    setLocalCarrierAddress(carrierAddress);
    setLocalPerClaimLimit(formatCurrency(perClaimLimit));
    setLocalAggregateLimit(formatCurrency(aggregateLimit));
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
    setLocalNotes(notes);
    setLocalArchived(archived);
    setInitialDocumentFilenames();

    if (onCancelEdit)
    {
      onCancelEdit();
    }
  }

  function saveEdit(e) {
    e.preventDefault();
    if (localCarrierName === '')
    {
      alert("Please enter carrier name");
      return;
    }
    if (localPolicy === '' && localStartDate === null)
    {
      alert("Please enter a policy or start date");
      return;
    }

    setEditing(false);

    authenticatedFetch({
      method: id ? "PUT" : "POST",
      requestData: {providerId: parseInt(providerId), carrierName: localCarrierName, policy: localPolicy, carrierAddress: localCarrierAddress, perClaimLimit: localPerClaimLimit.replace(/[^\d.]/g, ""), aggregateLimit: localAggregateLimit.replace(/[^\d.]/g, ""), startDate: localStartDate, endDate: localEndDate, notes: localNotes, archived: localArchived, documentFilenames: documentFilenames},
      path: id ? "malpracticeinsurance/" + id  : "malpracticeinsurance",
      successHandler: function(result) {
        refreshList();
        setDocumentsRefresh(documentsRefresh + 1);
        notyf.open({type: "success", message: id ? "Updated!" : "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function() {
        setEditing(true);
        setLocalCarrierName(carrierName);
        setLocalPolicy(policy);
        setLocalCarrierAddress(carrierAddress);
        setLocalPerClaimLimit(formatCurrency(perClaimLimit));
        setLocalAggregateLimit(formatCurrency(aggregateLimit));
        setLocalStartDate(startDate);
        setLocalEndDate(endDate);
        setLocalNotes(notes);
        setLocalArchived(archived);
        setInitialDocumentFilenames();
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });    
  }

  function handleCarrierChanged(e) {
    e.preventDefault();
    setLocalCarrierName(e.target.value);
  }

  function handlePolicyChanged(e) {
    e.preventDefault();
    setLocalPolicy(e.target.value);
  }

  function handleCarrierAddressChanged(e) {
    e.preventDefault();
    setLocalCarrierAddress(e.target.value);
  }
  
  function handlePerClaimLimitChanged(e) {
    e.preventDefault();
    setLocalPerClaimLimit(formatCurrency(e.target.value));
  }

  function handleAggregateLimitChanged(e) {
    e.preventDefault();
    setLocalAggregateLimit(formatCurrency(e.target.value));
  }

  function handleStartDateChanged(val, e) {
    e.preventDefault();
    setLocalStartDate(val);
  }

  function handleEndDateChanged(val, e) {
    e.preventDefault();
    setLocalEndDate(val);
  }

  function handleNotesChanged(e) {
    e.preventDefault();
    setLocalNotes(e.target.value);
  }

  function handleArchivedChanged(e) {
    setLocalArchived(e.target.checked);
  }

  var notesReadOnly = '';
  if (localNotes && localNotes.length > 0) {
    notesReadOnly = localNotes.split("\n").map(function(item, idx) {
      return ( 
          <span key={idx}>
              {item}
              <br />
          </span>
       )
    });
  }

  var carrierAddressReadOnly = '';
  if (localCarrierAddress && localCarrierAddress.length > 0) {
    carrierAddressReadOnly = localCarrierAddress.split("\n").map(function(item, idx) {
      return ( 
          <span key={idx}>
              {item}
              <br />
          </span>
       )
    }
    );
  }

  return (
    <>
    {editing ?
    <td>
      <Form.Control size="sm" style={{minWidth: "100px"}} autoFocus={true} maxLength={255} value={localCarrierName} onChange={(e) => {handleCarrierChanged(e)}} />
    </td>
    :
    <td style={
      !localCarrierName || localCarrierName.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
    }>
      {localCarrierName}
    </td>
    }
    {editing ?
    <td>
      <Form.Control size="sm" style={{minWidth: "100px"}} maxLength={255} value={localPolicy} onChange={(e) => {handlePolicyChanged(e)}} />
    </td>
    :
    <td style={
      !localPolicy || localPolicy.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
    }>
      {localPolicy}
    </td>
    }
    <td>
    <EditableExpires warnWithColors={false} localExpires={localStartDate} localActive={true} editingExpires={editing} handleExpiresChanged={handleStartDateChanged} blankText=""/>
    </td>
    <td style={
      !localEndDate || localEndDate.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
    }>
    <EditableExpires localExpires={localEndDate} localActive={true} editingExpires={editing} handleExpiresChanged={handleEndDateChanged} blankText=""/>
    </td>
    {editing ?
    <td>
      Per Claim Limit: <div style={{ display: "flex", alignItems: "center" }}><span style={{ marginRight: 4 }}>$</span><Form.Control size="sm" style={{minWidth: "130px"}} maxLength={255} value={localPerClaimLimit} onChange={(e) => {handlePerClaimLimitChanged(e)}} /></div>
      Aggregate Limit: <div style={{ display: "flex", alignItems: "center" }}><span style={{ marginRight: 4 }}>$</span><Form.Control size="sm" style={{minWidth: "130px"}} maxLength={255} value={localAggregateLimit} onChange={(e) => {handleAggregateLimitChanged(e)}} /></div>
      Carrier Address: <Form.Control style={{height: "80px"}} size="sm" as="textarea" maxLength={511} value={localCarrierAddress} onChange={(e) => {handleCarrierAddressChanged(e)}} />
      Other: <Form.Control style={{height: "80px"}} size="sm" as="textarea" maxLength={511} value={localNotes} onChange={(e) => {handleNotesChanged(e)}} />
    </td>
    :
    <td>
      {localPerClaimLimit !== "" ? <>PCL: ${localPerClaimLimit}<br/></> : <></>}
      {localAggregateLimit !== "" ? <>AL: ${localAggregateLimit}<br/></> : <></>}
      {carrierAddressReadOnly}
      {notesReadOnly}
    </td>
    }
    {editing?
    <td> 
      <Form.Check type="checkbox" checked={localArchived} onChange={(e) => {handleArchivedChanged(e)}} />
    </td>
    :
    <td>
      {localArchived ? <Watch style={{'height': '16px', 'width': '16px'}}/> : <>&nbsp;</>}
    </td>
    }
    {(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))&&
    <>
      {editing && (isH3Admin || authorities.includes("PROVIDER_DOCUMENT_EDIT"))?
      <td>
        <LinkedDocumentSelect entityType={"malpracticeinsurance"} providerId={providerId} handleDocumentChanged={handleDocumentChanged} selectedDocuments={selectedDocuments}/>
      </td>
        : 
      <td>
        <LinkedDocumentsDisplay entityType={"malpracticeinsurance"} documents={documents} entityId={id}/>
      </td> 
      }
    </>
    }
    <td>{getReminderSentDisplay(reminderSent)}</td>
    <td style={{width: '70px'}}>
      { canEdit?
        <>
        {editing ? 
        <>
          <X style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {cancelEdit(e)}} />
          &nbsp;<Check style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {saveEdit(e)}} className="text-success"/>
        </>
        :
        <>
          <Edit2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleEditClicked(e)}}/>&nbsp;&nbsp;
          <Trash2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleDeleteClicked(e)}}/>
        </>
        }
        </>
        : <>&nbsp;</>
      }
    </td>
  </>
  )
}


const MalpracticeInsurancesTable = ({providerId, addingNew, onCancelAddNew, list, isLoaded, error, canEdit, refreshList}) => {
  const data = list;

  const { isH3Admin, authorities } = useContext(UserSettingsContext);

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        accessor: "id",
        key: "id",
      },
      {
        Header: "Carrier",
        accessor: "carrierName",
        key: "carrierName",
      },
      {
        Header: "Policy",
        accessor: "policy",
        key: "policy",
      },
      {
        accessor: "carrierAddress",
        key: "carrierAddress",
      },
      {
        accessor: "perClaimLimit",
        key: "perClaimLimit",
      },
      {
        accessor: "aggregateLimit",
        key: "aggregateLimit",
      },
      {
        Header: "Start Date",
        accessor: "startDate",
        key: "startDate",
      },
      {
        Header: "End Date",
        accessor: "endDate",
        key: "endDate",
        Filter: ExpiresColumnCheckboxFilter,
        filter: customExpiresFilter("endDate")
      },
      {
        Header: "Details",
        accessor: "notes",
        key: "notes",
      },
      {
        Header: "Not Renewed",
        accessor: "archived",
        key: "archived",
      }
    ];
    

    
    if(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))
    {
      baseColumns.push(
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Documents</Tooltip>}
          >
            <span>
              <File size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "documents",
        key: "documents",
      });
    }

    baseColumns.push(
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Reminder Sent</Tooltip>}
          >
            <span>
              <MessageSquare size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "reminderSent",
        key: "reminderSent",
      },
      {
        Header: "",
        accessor: "icons",
        key: "icons",
      },
    );
    return baseColumns;
  }, [isH3Admin, authorities]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
  useTable(
    {
      columns,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
      initialState: {
        hiddenColumns: ['id', 'perClaimLimit', 'aggregateLimit', 'carrierAddress'],
        sortBy: [
            {
                id: 'startDate',
                desc: true
            },
            {
              id: 'endDate',
              desc: true
            }
        ],
        filters: [
            {
                id: 'endDate',
                value: 'excludeGreaterThan30Days',
            },
        ],
      },
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => caseInsensitiveSort({row1, row2, columnName})
      }
    },
    useFilters,
    useSortBy,
  );

  // Extract the expires filter
  var expiresFilter = "";
  for (var i=0; i < headerGroups.length; i++)
  {
    for (var j=0; j < headerGroups[i].headers.length; j++)
    {
      var column = headerGroups[i].headers[j];
      if (column.id === "endDate")
      {
        expiresFilter = column.render("Filter");
        break;
      }
    }
  }

  return (
    <>
      <Table className="table-sm" striped bordered hover {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()} style={{whiteSpace: "nowrap"}}>
            {headerGroup.headers.map((column) => {
              const { key, ...restHeaderProps } = (column.id === "icons" || column.id === "notes" || column.id === "reminderSent" || column.id === "documents") ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps());
              return (
                <th {...restHeaderProps} key={key} style={column.id === "archived" ? {...restHeaderProps.style, whiteSpace: "normal"} : restHeaderProps.style}>
                  {column.render("Header")}
                  {(column.id !== "icons" && column.id !== "notes" && column.id !== "reminderSent" && column.id !== "documents") ? (
                    <>
                    {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                        ) : (
                          <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                        )
                      ) : (
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                    )}
                    </>
                  ) : <></>
                  }
                </th>
              )
            }
            )}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {
        error ? (<tr><td colSpan={9}>Something went wrong; please reload the page...</td></tr>) : ( 
          !isLoaded ? (<tr><td colSpan={9}>Loading...</td></tr>) : (
            rows.map((row) => {
            prepareRow(row);
            const { key, ...restRowProps } = row.getRowProps();
            return (
              <tr {...restRowProps} key={key}
                style={{
                  ...(row.values.archived ? {opacity: 0.5} : {}),
                  ...((row.values.notes && row.values.notes.toLowerCase().indexOf("please verify") >= 0) || (row.values.policy && row.values.policy.toLowerCase().indexOf("please verify") >= 0)
                    ? {
                        // td's read these from the tr; setting them here colors the whole row
                        ["--bs-table-bg"]: "rgba(255,193,7,0.3)",
                        // cancel the striped overlay so it doesn't stack on the warning color
                        ["--bs-table-striped-bg"]: "transparent",
                        ["--bs-table-accent-bg"]: "transparent"
                      }
                    : {})
                }}>
                <MalpracticeInsurance providerId={providerId} id={row.values.id} key={row.values.id} carrierName={row.values.carrierName ? row.values.carrierName : ""} policy={row.values.policy ? row.values.policy : ""} carrierAddress={row.values.carrierAddress ? row.values.carrierAddress : ""} perClaimLimit={row.values.perClaimLimit !== null && row.values.perClaimLimit !== undefined ? row.values.perClaimLimit : ""} aggregateLimit={row.values.aggregateLimit !== null && row.values.aggregateLimit !== undefined ? row.values.aggregateLimit : ""} startDate={row.values.startDate} endDate={row.values.endDate} notes={row.values.notes ? row.values.notes : ""} archived={row.values.archived == null ? false : row.values.archived} reminderSent={row.values.reminderSent ? row.values.reminderSent : ""} documents={row.values.documents}  refreshList={refreshList} canEdit={canEdit}/>
              </tr>
            );
          })))
        }
        {
        addingNew ?
          <tr><MalpracticeInsurance providerId={providerId} key={0} startInEditMode={true} carrierName={""} policy={""} carrierAddress={""} perClaimLimit={""} aggregateLimit={""} startDate={null} endDate={null} notes={""} archived={false} reminderSent={""} documents={[]} onCancelEdit={onCancelAddNew} canEdit={canEdit} refreshList={refreshList}/></tr>
        : <></>
        }
      </tbody>
    </Table>
    {expiresFilter}
  </>
  )
}

const MalpracticeInsurances = ({providerId, canEdit}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [addingNew, setAddingNew] = useState(false);

  const { documentsRefresh, tasksRefresh } = useContext(UserSettingsContext);

  useEffect(() => {
    refreshList();
  }, [providerId, documentsRefresh, tasksRefresh])

  function refreshList()
  {
    setAddingNew(false);
    authenticatedFetch({
      path: "malpracticeinsurance/provider/" + providerId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function() {
        setIsLoaded(true);
        setError(true);
      }
    });
  }

  const handleAddNewClicked = (e) => {
    e.preventDefault();
  
    setAddingNew(true);
  }

  const onCancelAddNew = () => {
    setAddingNew(false);
  }

  var list = [];
  if (isLoaded)
  {
    list = data.map((item, i) => {
      return {key: item.id, id: item.id, carrierName: item.carrierName, policy: item.policy, carrierAddress: item.carrierAddress, perClaimLimit: item.perClaimLimit, aggregateLimit: item.aggregateLimit, startDate: item.startDate, endDate: item.endDate, notes: item.notes, archived: item.archived, reminderSent: item.reminderSent, documents: item.documents, updated: item.updated, updatedBy: item.updatedBy}
    }, this);
  }

  return (
    <Card>
      <Card.Header style={{marginBottom: '-20px'}}>
        <Card.Title>Malpractice Insurance
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <MalpracticeInsurancesTable list={list} addingNew={addingNew} onCancelAddNew={onCancelAddNew} providerId={providerId} error={error} isLoaded={isLoaded} canEdit={canEdit} refreshList={refreshList}/>
        {canEdit?
          <span className="float-end" style={{marginTop: '6px'}}>
            <Button size="sm" variant="primary" className="float-end" onClick={(e) => {handleAddNewClicked(e)}}>+ Add New Malpractice Insurance</Button>
          </span>
          : <></>
        }
      </Card.Body>
    </Card>
  )
}

export default MalpracticeInsurances;
